import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/pro-regular-svg-icons";
// Back の山括弧は太めにしたいので solid を使用
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import LoginPage from "./pages/auth/LoginPage";
// import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
// import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

import TopEditPage from "./pages/website/top/TopEditPage";
import AboutEditPage from "./pages/website/about/AboutEditPage";
import PriceEditPage from "./pages/website/price/PriceEditPage";
import AccessEditPage from "./pages/website/access/AccessEditPage";
import InstructorEditPage from "./pages/website/instructor/InstructorEditPage";
// Phase 6-A: レッスン管理を /lesson に一本化（3層モデルの正式 UI）。
// 旧 LessonContentEditPage / LessonEditPage は廃止し、説明文・色凡例編集は
// LessonManagementPage 内のモーダルに統合した。
import LessonManagementPage from "./pages/website/lesson/LessonManagementPage.js";
import NewVisitorEditPage from "./pages/website/newvisitor/NewVisitorEditPage";
import ReserveConfirmEditPage from "./pages/website/reserve_confirm/ReserveConfirmEditPage.js";
import NewsEditPage from "./pages/website/news/NewsEditPage";
import TermsEditPage from "./pages/website/terms/TermsEditPage";
import PrivacyEditPage from "./pages/website/privacy/PrivacyEditPage";
import TrialListPage from "./pages/management/trial/TrialList.js";
import CustomerListPage from "./pages/management/customers/CustomerList.js";
import MembershipEditPage from "./pages/website/membership/MembershipEditPage.js";
import MembershipThanksEditPage from "./pages/website/membership/MembershipThanksEditPage.js";
import AnalyticsPage from "./pages/management/analytics/AnalyticsPage.js";
import SchedulePage from "./pages/management/schedule/SchedulePage.js";
// Phase 7-G 着手前の挙動確認用（URL 直打ち専用、サイドバー非掲載）
import HtmlToImageDemo from "./pages/management/schedule/HtmlToImageDemo.js";
// 発表会モック（サイドバー「Recital」から遷移・各ページは未公開）
import RecitalSettingPage from "./pages/recital/2026/RecitalSettingPage.js";
import RecitalProgramPage from "./pages/recital/2026/RecitalProgramPage.js";
import RecitalEntryListPage from "./pages/recital/2026/RecitalEntryListPage.js";
import RecitalOrderPage from "./pages/recital/2026/RecitalOrderPage.js";
import RecitalRollcallPage from "./pages/recital/2026/RecitalRollcallPage.js";
import RecitalPhotoPage from "./pages/recital/2026/RecitalPhotoPage.js";

import useBodyClass from "./hooks/useBodyClass";
import GradientCanvas from "./components/GradientCanvas.js";

function AppContent() {
  // ログイン状態
  const { isLoggedIn, isSuperuser, username, logout } = useAuth();
  // 現在のURL
  const location = useLocation();

  // サイドバーの開閉状態
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // サイドバーのメニュー切替：現在地が /recital 配下なら発表会メニュー、それ以外は通常メニュー。
  // URL に追従させることで Home/Back などの遷移でメニュー表示がズレないようにする。
  const recitalMenu = location.pathname.startsWith("/recital");

  // useBodyClassを最上位で呼び出し
  useBodyClass();

  // 動的ID管理
  const [firstStudioId, setFirstStudioId] = useState(1);

  // 最小IDを取得する関数
  const fetchFirstIds = async () => {
    if (!isLoggedIn) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // スタジオの最小IDを取得
      const studioResponse = await fetch(`${apiUrl}/api/get_first_id/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type: "studio" }),
      });
      if (studioResponse.ok) {
        // スタジオの最小IDを取得
        const studioData = await studioResponse.json();
        if (studioData.id) {
          setFirstStudioId(studioData.id);
        }
      }
    } catch (error) {
      console.error("IDの取得に失敗しました:", error);
    }
  };

  // ログイン状態が変わったら最小IDを取得
  useEffect(() => {
    fetchFirstIds();
  }, [isLoggedIn]);

  // 画面（メモ化されたコンポーネント）
  const RenderPage = useCallback(() => {
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    return (
      <>
        <main>
          <Routes>
            <Route path="/" element={<TopEditPage />} />
            <Route path="/about" element={<AboutEditPage />} />
            <Route path="/price" element={<PriceEditPage />} />
            <Route path="/access" element={<AccessEditPage />} />
            <Route path="/instructor" element={<InstructorEditPage />} />
            <Route path="/newvisitor" element={<NewVisitorEditPage />} />
            <Route path="/news" element={<NewsEditPage />} />
            <Route path="/news/:newsId" element={<NewsEditPage />} />
            <Route
              path="/reserve_confirm/:studioId"
              element={<ReserveConfirmEditPage />}
            />
            <Route path="/terms" element={<TermsEditPage />} />
            <Route path="/privacy" element={<PrivacyEditPage />} />
            <Route path="/membership" element={<MembershipEditPage />} />
            <Route
              path="/membership/thanks"
              element={<MembershipThanksEditPage />}
            />
            <Route path="/trial" element={<TrialListPage />} />
            <Route path="/customers" element={<CustomerListPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            {/* Phase 6-A: レッスン管理を /lesson に一本化 */}
            <Route path="/lesson" element={<LessonManagementPage />} />
            {/* 旧 /lesson-v2 ブックマーク等の後方互換リダイレクト */}
            <Route path="/lesson-v2" element={<Navigate to="/lesson" replace />} />
            {/* Phase 7-G 着手前の挙動確認用。URL 直打ち専用 */}
            <Route path="/html-to-image-demo" element={<HtmlToImageDemo />} />
            {/* 発表会モック */}
            <Route path="/recital/2026/setting" element={<RecitalSettingPage />} />
            <Route path="/recital/2026/program" element={<RecitalProgramPage />} />
            <Route path="/recital/2026/entries" element={<RecitalEntryListPage />} />
            <Route path="/recital/2026/order" element={<RecitalOrderPage />} />
            <Route path="/recital/2026/rollcall" element={<RecitalRollcallPage />} />
            <Route path="/recital/2026/photos" element={<RecitalPhotoPage />} />
          </Routes>
        </main>
      </>
    );
  }, [isLoggedIn, firstStudioId]);

  // 認証関連のページかどうかを判定
  const isAuthPage =
    ["/login", "/forgot-password"].includes(location.pathname) ||
    location.pathname.startsWith("/reset-password");

  // 発表会メニュー（サイドバー非掲載のページ群への導線。最下部の Back で通常メニューへ戻る）
  // §10.7：音源/照明の提出状況は website 公開ページへ移行（admin 自前表示は廃止）＝外部リンク
  const RECITAL_ITEMS = [
    ["/recital/2026/setting", "Settings"],
    ["/recital/2026/entries", "Entry List"],
    ["/recital/2026/program", "Programs"],
    ["/recital/2026/order", "Program Order"],
    ["/recital/2026/rollcall", "Name List"],
    [`${process.env.REACT_APP_ORIGIN}/recital/2026/submissions`, "Music / Lighting", true],
    ["/recital/2026/photos", "Photos"],
  ];
  const recitalNav = (
    <>
      {RECITAL_ITEMS.map(([to, label, external]) => (
        <li key={to}>
          {external ? (
            <a href={to} target="_blank" rel="noreferrer" onClick={() => setIsSidebarOpen(false)}>
              {label}
            </a>
          ) : (
            <Link to={to} onClick={() => setIsSidebarOpen(false)}>
              {label}
            </Link>
          )}
        </li>
      ))}
      <li>
        <Link
          to="/"
          onClick={() => setIsSidebarOpen(false)}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "0.8em" }} />
          Back
        </Link>
      </li>
    </>
  );

  return (
    <>
      <div className="wrapper">
        {isLoggedIn && !isAuthPage && (
          <>
            <nav className={`sidebar ${isSidebarOpen ? "sidebar-open" : ""}`}>
              <h1>IVY DANCE STUDIO</h1>
              <ul>
                {recitalMenu ? (
                  recitalNav
                ) : (
                <>
                <li>
                  <Link to="/" onClick={() => setIsSidebarOpen(false)}>
                    Top
                  </Link>
                </li>

                <li>
                  <Link to="/about" onClick={() => setIsSidebarOpen(false)}>
                    About
                  </Link>
                </li>

                <li>
                  <Link to="/price" onClick={() => setIsSidebarOpen(false)}>
                    Price
                  </Link>
                </li>

                <li>
                  <Link to="/access" onClick={() => setIsSidebarOpen(false)}>
                    Access
                  </Link>
                </li>

                <li>
                  <Link
                    to="/instructor"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    Instructor
                  </Link>
                </li>

                <li>
                  <Link to="/lesson" onClick={() => setIsSidebarOpen(false)}>
                    Lesson
                  </Link>
                </li>

                <li>
                  <Link
                    to="/newvisitor"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    New Visitor
                  </Link>
                </li>

                <li>
                  <Link to="/news" onClick={() => setIsSidebarOpen(false)}>
                    News
                  </Link>
                </li>

                <li>
                  <Link
                    to="/reserve_confirm/1"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    Reserve Confirm
                  </Link>
                </li>

                <li>
                  <Link to="/terms" onClick={() => setIsSidebarOpen(false)}>
                    Terms
                  </Link>
                </li>

                <li>
                  <Link to="/privacy" onClick={() => setIsSidebarOpen(false)}>
                    Privacy
                  </Link>
                </li>

                <li>
                  <Link to="/membership" onClick={() => setIsSidebarOpen(false)}>
                    Membership
                  </Link>
                </li>

                <li>
                  <Link to="/schedule" onClick={() => setIsSidebarOpen(false)}>
                    Schedule
                  </Link>
                </li>

                <li>
                  <Link to="/trial" onClick={() => setIsSidebarOpen(false)}>
                    Trial List
                  </Link>
                </li>

                <li>
                  <Link to="/customers" onClick={() => setIsSidebarOpen(false)}>
                    Customer List
                  </Link>
                </li>

                <li>
                  <Link to="/analytics" onClick={() => setIsSidebarOpen(false)}>
                    Analytics
                  </Link>
                </li>

                {/* 発表会(Recital)。クリックで /recital/2026/... 配下の発表会メニューに切り替わる。 */}
                <li>
                  <Link
                    to="/recital/2026/setting"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    Recital
                  </Link>
                </li>

                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.confirm("ログアウトしますか？")) {
                        logout();
                      }
                    }}
                  >
                    Logout
                  </Link>
                </li>
                </>
                )}
              </ul>
            </nav>
            <div className="headder">
              <div
                className="hamburger-menu"
                onClick={(e) => {
                  e.preventDefault();
                  setIsSidebarOpen(!isSidebarOpen);
                }}
              >
                <FontAwesomeIcon icon={faBars} className="menu-btn-icon" />
              </div>
              <p className="username">{username || "admin"}</p>
            </div>
            {isSidebarOpen && (
              <div
                className="sidebar-overlay"
                onClick={() => setIsSidebarOpen(false)}
              ></div>
            )}
          </>
        )}
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* <Route path="/signup" element={<SignUpPage />} /> */}
          {/* <Route path="/forgot-password" element={<ForgotPasswordPage />} /> */}
          {/* <Route
            path="/reset-password/:uid/:token"
            element={<ResetPasswordPage />}
          /> */}
          <Route path="*" element={<RenderPage />} />
        </Routes>
        <GradientCanvas />
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
